import { notify } from '@/lib/toast';
import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Package, Download, ChevronRight, Tag as TagIcon,
  Hash, Check, X, Pencil, Warehouse, ChefHat, SlidersHorizontal, Search,
} from 'lucide-react';
import { Dialog } from 'primereact/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { EmptyState } from '@/components/ui/empty-state';
import { PaginationBar } from '@/components/ui/pagination-bar';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';

import { useAuthStore } from '@/stores/auth.store';
import api from '@/lib/api';
import toast from 'react-hot-toast';
import { cn, formatCurrency } from '@/lib/utils';
import { countOf, plural } from '@/lib/plural';

// ── Types ──────────────────────────────────────────────────────────────────

interface TemplateTaxRate {
  id: string;
  name: string;
  rate: number;
  type: 'INCLUSIVE' | 'EXCLUSIVE';
}

interface TemplateCategory {
  id: string;
  name: string;
  slug: string;
  description?: string;
  taxes: { taxRate: TemplateTaxRate }[];
  children: TemplateCategory[];
}

interface TemplateProduct {
  id: string;
  name: string;
  sku: string;
  barcode?: string;
  hsCode?: string;
  uom: string;
  salePrice: number;
  costPrice: number;
  unit: string;
  fbrScenarioId?: string;
  taxOverride: boolean;
  trackStock?: boolean;
  category?: TemplateCategory;
  taxes: { taxRate: TemplateTaxRate }[];
}

interface ProductsResponse {
  data: TemplateProduct[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

interface ProductOverride {
  productId: string;
  costPrice: number;
  salePrice: number;
  stockQuantity: number;
  barcode: string;
}

/** Import request row: barcode is omitted when blank. */
type ProductImportPayloadRow = Omit<ProductOverride, 'barcode'> & { barcode?: string };

// ── Main Component ─────────────────────────────────────────────────────────

export function ProductImportTemplatePage() {
  const { hasPermission } = useAuthStore();
  const queryClient = useQueryClient();

  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState<TemplateProduct[]>([]);
  const [mobileShowCategories, setMobileShowCategories] = useState(false);
  const [showImportResult, setShowImportResult] = useState<{
    created: number;
    skipped: number;
    errors: string[];
  } | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [overrides, setOverrides] = useState<Map<string, ProductOverride>>(new Map());

  // Debounce search
  const handleSearchChange = (val: string) => {
    setSearch(val);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(val);
      setPage(1);
    }, 400);
  };

  const handleCategoryChange = (slug: string | undefined) => {
    setSelectedCategory(slug);
    setPage(1);
    setSelectedRows([]);
    setMobileShowCategories(false); // close mobile overlay after selection
  };

  // ── Data Queries ───────────────────────────────────────────────────────

  const { data: categories = [] } = useQuery<TemplateCategory[]>({
    queryKey: ['template-categories'],
    queryFn: () => api.get('/product-templates/categories').then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  });

  const { data: productsData, isFetching } = useQuery<ProductsResponse>({
    queryKey: ['template-products', selectedCategory, debouncedSearch, page],
    queryFn: () =>
      api
        .get('/product-templates/products', {
          params: { category: selectedCategory, search: debouncedSearch || undefined, page, limit: 50 },
        })
        .then((r) => r.data),
    staleTime: 60_000,
  });

  // ── Import Mutation ────────────────────────────────────────────────────

  const { t: tInv } = useTranslation('inventory');
  const [confirmLarge, setConfirmLarge] = useState(false);

  const importMutation = useMutation({
    mutationFn: (payload: ProductImportPayloadRow[]) =>
      api
        .post('/product-templates/import', { products: payload, ...(confirmLarge ? { confirmLargeValue: true } : {}) })
        .then((r) => r.data),
    onSuccess: (result) => {
      setShowImportResult(result);
      setSelectedRows([]);
      setShowDialog(false);
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      if (result.created > 0) toast.success(`✅ Imported ${countOf(result.created, 'product', 'products')} successfully`);
      if (result.skipped > 0) toast(`⏭ ${result.skipped} ${plural(result.skipped, 'product already existed', 'products already existed')} — skipped`, { icon: 'ℹ️' });
    },
    onError: (err) => notify.error(err, 'Import failed'),
  });

  const handleOpenDialog = () => {
    const initialOverrides = new Map<string, ProductOverride>();
    for (const p of selectedRows) {
      initialOverrides.set(p.id, {
        productId: p.id,
        costPrice: Number(p.costPrice),
        salePrice: Number(p.salePrice),
        stockQuantity: 0,
        barcode: p.barcode ?? '',
      });
    }
    setOverrides(initialOverrides);
    setConfirmLarge(false);
    setShowDialog(true);
  };

  const handleOverrideChange = (
    id: string,
    field: keyof Omit<ProductOverride, 'productId'>,
    value: number | string,
  ) => {
    setOverrides((prev) => {
      const next = new Map(prev);
      const existing = next.get(id)!;
      next.set(id, { ...existing, [field]: value });
      return next;
    });
  };

  const handleConfirmImport = () => {
    const payload = Array.from(overrides.values()).map(({ barcode, ...rest }) => ({
      ...rest,
      ...(barcode ? { barcode } : {}),
    }));
    importMutation.mutate(payload);
  };

  const products = productsData?.data ?? [];
  const meta = productsData?.meta;

  // ── Selection (full row objects so it survives page changes) ──────────

  const isSelected = (id: string) => selectedRows.some((r) => r.id === id);
  const toggleRow = (row: TemplateProduct) =>
    setSelectedRows((prev) =>
      prev.some((r) => r.id === row.id) ? prev.filter((r) => r.id !== row.id) : [...prev, row],
    );
  const allPageSelected = products.length > 0 && products.every((p) => isSelected(p.id));
  const togglePage = () =>
    setSelectedRows((prev) => {
      if (allPageSelected) {
        const pageIds = new Set(products.map((p) => p.id));
        return prev.filter((r) => !pageIds.has(r.id));
      }
      const have = new Set(prev.map((r) => r.id));
      return [...prev, ...products.filter((p) => !have.has(p.id))];
    });

  /** Native number input to stored number (blank gives 0, matching the old InputNumber `e.value ?? 0`). */
  const toNumber = (raw: string) => (raw === '' ? 0 : Number(raw));

  // ── DataTable column body templates ───────────────────────────────────

  const productBody = (row: TemplateProduct) => (
    <div>
      <div className="font-medium text-sm leading-tight">{row.name}</div>
      {row.category && (
        <div className="text-xs text-muted-foreground mt-0.5">{row.category.name}</div>
      )}
    </div>
  );

  const skuBody = (row: TemplateProduct) => (
    <span className="font-mono text-xs text-muted-foreground">{row.sku}</span>
  );

  const hsCodeBody = (row: TemplateProduct) =>
    row.hsCode ? (
      <span className="flex items-center gap-1 text-xs">
        <Hash className="h-3 w-3" />
        {row.hsCode}
      </span>
    ) : (
      <span className="text-xs text-muted-foreground">—</span>
    );

  const categoryBody = (row: TemplateProduct) =>
    row.category ? (
      <span className="flex items-center gap-1 text-xs">
        <TagIcon className="h-3 w-3" />
        {row.category.name}
      </span>
    ) : (
      <span className="text-xs text-muted-foreground">—</span>
    );

  const taxBody = (row: TemplateProduct) => {
    const taxes =
      row.taxOverride && row.taxes.length > 0
        ? row.taxes.map((t) => t.taxRate)
        : row.category?.taxes.map((t) => t.taxRate) ?? [];
    return (
      <div className="flex flex-wrap gap-1">
        {taxes.map((t) => (
          <span
            key={t.id}
            className="inline-flex items-center rounded-full bg-info/10 px-2 py-0.5 text-[10px] font-medium text-info-text"
          >
            {t.name}{t.name.includes('%') ? '' : ` ${Number(t.rate)}%`}
          </span>
        ))}
        {taxes.length === 0 && <span className="text-xs text-muted-foreground">—</span>}
      </div>
    );
  };

  const salePriceBody = (row: TemplateProduct) => (
    <span className="tabular-nums text-sm block text-end">
      PKR {Number(row.salePrice).toLocaleString()}
    </span>
  );

  const costPriceBody = (row: TemplateProduct) => (
    <span className="tabular-nums text-sm text-muted-foreground block text-end">
      PKR {Number(row.costPrice).toLocaleString()}
    </span>
  );

  const uomBody = (row: TemplateProduct) =>
    row.uom ? (
      <span className="inline-flex items-center rounded px-1.5 py-0.5 text-xs font-mono font-medium bg-muted text-muted-foreground ring-1 ring-inset ring-border">
        {row.uom}
      </span>
    ) : (
      <span className="text-xs text-muted-foreground">—</span>
    );

  const trackedBody = (row: TemplateProduct) =>
    row.trackStock === false ? (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-warning/10 text-warning-text border border-warning/30">
        <ChefHat className="h-3 w-3" />Prepared
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-success/10 text-success-text border border-success/30">
        <Warehouse className="h-3 w-3" />Tracked
      </span>
    );

  // ── Adjustment Dialog header & footer ─────────────────────────────────

  const dialogHeader = (
    <div className="flex items-center gap-2">
      <Pencil className="h-5 w-5 text-primary-text" />
      <div>
        <p className="text-base font-semibold">Adjust Before Import</p>
        <p className="text-xs text-muted-foreground">
          Set cost price, sale price, and initial stock for {countOf(selectedRows.length, 'product', 'products')}
        </p>
      </div>
    </div>
  );

  // POS-I237: rows whose opening stock is worth Rs 100,000 or more need one explicit confirmation
  const largeRows = selectedRows
    .map((row) => {
      const o = overrides.get(row.id);
      return { name: row.name, value: Math.round((o?.stockQuantity ?? 0) * (o?.costPrice ?? 0) * 100) / 100 };
    })
    .filter((r) => r.value >= 100_000);

  const dialogFooter = (
    <div className="flex items-center justify-between w-full">
      <p className="text-xs text-muted-foreground">
        Template prices shown as defaults — adjust per your business needs
      </p>
      <div className="flex gap-2">
        <Button
          variant="outline"
          onClick={() => setShowDialog(false)}
          disabled={importMutation.isPending}
        >
          Cancel
        </Button>
        <Button onClick={handleConfirmImport} loading={importMutation.isPending} disabled={largeRows.length > 0 && !confirmLarge}>
          {!importMutation.isPending && <Download className="h-4 w-4" />}
          {`Confirm Import (${selectedRows.length})`}
        </Button>
      </div>
    </div>
  );

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <>
      {/* ── Adjustment Dialog ─────────────────────────────────────────── */}
      <Dialog
        header={dialogHeader}
        visible={showDialog}
        onHide={() => !importMutation.isPending && setShowDialog(false)}
        style={{ width: '56rem' }}
        maximizable
        footer={dialogFooter}
        contentClassName="p-0"
      >
        {largeRows.length > 0 && (
          <div className="m-4 space-y-2 rounded-md border border-warning/40 bg-warning/10 p-3 text-sm" data-testid="import-large-rows">
            <p className="font-medium">{tInv('importLargeRows')}</p>
            <ul className="list-disc ps-5 text-xs">
              {largeRows.map((r) => (
                <li key={r.name}>
                  <span title={r.name}>{r.name}</span> <span dir="ltr" className="tabular-nums">{formatCurrency(r.value)}</span>
                </li>
              ))}
            </ul>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={confirmLarge} onChange={(e) => setConfirmLarge(e.target.checked)} />
              <span>{tInv('importConfirmRows')}</span>
            </label>
          </div>
        )}
        <div className="max-h-[420px] overflow-y-auto">
          <Table>
            <THead className="sticky top-0 bg-background">
              <Tr hover={false}>
                <Th>Product</Th>
                <Th className="w-32">SKU</Th>
                <Th className="w-44">Cost Price (PKR)</Th>
                <Th className="w-44">Sale Price (PKR)</Th>
                <Th className="w-36">Initial Stock</Th>
                <Th className="w-44">Barcode</Th>
              </Tr>
            </THead>
            <TBody>
              {selectedRows.map((row) => {
                const o = overrides.get(row.id);
                return (
                  <Tr key={row.id}>
                    <Td>{productBody(row)}</Td>
                    <Td>{skuBody(row)}</Td>
                    <Td>
                      <Input
                        type="number"
                        step="0.01"
                        value={o?.costPrice ?? 0}
                        onChange={(e) => handleOverrideChange(row.id, 'costPrice', toNumber(e.target.value))}
                        className="w-full text-end"
                      />
                    </Td>
                    <Td>
                      <Input
                        type="number"
                        step="0.01"
                        value={o?.salePrice ?? 0}
                        onChange={(e) => handleOverrideChange(row.id, 'salePrice', toNumber(e.target.value))}
                        className="w-full text-end"
                      />
                    </Td>
                    <Td>
                      <Input
                        type="number"
                        step="1"
                        min={0}
                        value={o?.stockQuantity ?? 0}
                        onChange={(e) => handleOverrideChange(row.id, 'stockQuantity', Math.max(0, Math.trunc(toNumber(e.target.value))))}
                        className="w-full text-end"
                      />
                    </Td>
                    <Td>
                      <Input
                        value={o?.barcode ?? ''}
                        onChange={(e) => handleOverrideChange(row.id, 'barcode', e.target.value)}
                        placeholder={row.barcode ?? 'optional'}
                        className="w-full text-xs"
                      />
                    </Td>
                  </Tr>
                );
              })}
            </TBody>
          </Table>
        </div>
      </Dialog>

      {/* ── Floating Selection Bar ────────────────────────────────────── */}
      {selectedRows.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 rounded-xl bg-primary px-5 py-3 shadow-2xl shadow-primary/30 text-primary-foreground">
          <Check className="h-4 w-4 shrink-0" />
          <span className="text-sm font-medium">
            {countOf(selectedRows.length, 'product', 'products')} selected
          </span>
          {hasPermission('products:write') ? (
            <Button size="sm" variant="secondary" onClick={handleOpenDialog} className="ms-1">
              <Download className="h-4 w-4" />
              Import Selected
            </Button>
          ) : (
            <span className="ms-1 text-xs opacity-70">No import permission</span>
          )}
          <button
            onClick={() => setSelectedRows([])}
            className="ms-1 rounded-md p-0.5 opacity-70 transition-opacity hover:opacity-100"
            title="Clear selection"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* ── Two-Column Layout ─────────────────────────────────────────── */}
      <div className="flex -mx-6 -mt-6">
        {/* Mobile backdrop for category overlay */}
        {mobileShowCategories && (
          <div
            className="fixed inset-0 bg-scrim/50 z-40 lg:hidden"
            onClick={() => setMobileShowCategories(false)}
          />
        )}

        {/* Category Sidebar — desktop: in-flow sticky; mobile: fixed overlay */}
        <aside
          className={cn(
            'flex flex-col border-e bg-background p-3 space-y-1',
            // Desktop: always visible, in-flow
            'lg:w-56 lg:shrink-0 lg:sticky lg:top-0 lg:self-start lg:h-screen lg:overflow-y-auto lg:flex',
            // Mobile: hidden by default, shown as a sliding overlay
            mobileShowCategories
              ? 'fixed inset-y-0 start-0 z-50 w-72 overflow-y-auto shadow-2xl flex'
              : 'hidden lg:flex',
          )}
        >
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-2 pb-1 pt-1 flex items-center justify-between">
            <span>Categories</span>
            {/* Close button — mobile only */}
            <button
              className="lg:hidden text-muted-foreground hover:text-muted-foreground p-1 rounded"
              onClick={() => setMobileShowCategories(false)}
            >
              <X className="h-4 w-4" />
            </button>
          </p>
          <button
            onClick={() => handleCategoryChange(undefined)}
            className={cn(
              'w-full text-start px-3 py-2 rounded-lg text-sm transition-colors',
              !selectedCategory
                ? 'bg-primary text-primary-foreground'
                : 'hover:bg-muted text-muted-foreground',
            )}
          >
            All Products
          </button>
          {categories.map((cat) => (
            <div key={cat.id}>
              <button
                onClick={() => handleCategoryChange(cat.slug)}
                className={cn(
                  'w-full text-start px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-1',
                  selectedCategory === cat.slug
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted text-muted-foreground',
                )}
              >
                <ChevronRight className="h-3 w-3 shrink-0" />
                {cat.name}
              </button>
              {cat.children?.map((child) => (
                <button
                  key={child.id}
                  onClick={() => handleCategoryChange(child.slug)}
                  className={cn(
                    'w-full text-start ps-7 pe-3 py-1.5 rounded-lg text-xs transition-colors',
                    selectedCategory === child.slug
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-muted text-muted-foreground',
                  )}
                >
                  {child.name}
                </button>
              ))}
            </div>
          ))}
        </aside>

        {/* Main Content */}
        <div className="flex-1 min-w-0 flex flex-col">
          {/* Sticky header */}
          <div className="sticky top-0 z-10 border-b px-4 sm:px-6 py-3 sm:py-4 bg-background/95 backdrop-blur-sm">
            <div className="flex items-center justify-between mb-3 gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <Package className="h-5 w-5 text-primary-text shrink-0" />
                <div className="min-w-0">
                  <h1 className="text-base sm:text-lg font-semibold leading-tight">Product Import Templates</h1>
                  <p className="text-xs text-muted-foreground truncate">
                    Pakistan market catalog — {meta?.total ?? '…'} products across {categories.length} categories
                  </p>
                </div>
              </div>
              {hasPermission('products:write') && (
                <Button
                  onClick={handleOpenDialog}
                  disabled={selectedRows.length === 0}
                  size="sm"
                  className="shrink-0"
                >
                  <Download className="h-4 w-4" />
                  {selectedRows.length > 0 ? `Import (${selectedRows.length})` : 'Select to import'}
                </Button>
              )}
            </div>

            {/* Mobile: category filter button + active category chip */}
            <div className="flex items-center gap-2 mb-2 lg:hidden">
              <button
                onClick={() => setMobileShowCategories(true)}
                className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted transition-colors shrink-0"
              >
                <SlidersHorizontal className="h-3.5 w-3.5" />
                Category
              </button>
              {selectedCategory && (
                <span className="flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary-text">
                  {categories.find((c) => c.slug === selectedCategory)?.name ??
                    categories.flatMap((c) => c.children).find((c) => c.slug === selectedCategory)?.name ??
                    selectedCategory}
                  <button onClick={() => handleCategoryChange(undefined)}>
                    <X className="h-3 w-3 ms-0.5" />
                  </button>
                </span>
              )}
            </div>

            {/* Search */}
            <div className="relative w-full sm:max-w-md">
              <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Search by name, SKU or HS code…"
                className="ps-9"
              />
            </div>
          </div>

          {/* Import result banner */}
          {showImportResult && (
            <div className="mx-6 mt-4 flex items-start gap-3 rounded-lg border border-success/30 bg-success/10 p-4">
              <Check className="h-5 w-5 text-success-text shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <p className="font-medium text-success-text">
                  Import complete — {showImportResult.created} created, {showImportResult.skipped} skipped
                </p>
                {showImportResult.errors.length > 0 && (
                  <ul className="mt-1 text-xs text-destructive-text space-y-0.5">
                    {showImportResult.errors.map((e, i) => (
                      <li key={i}>⚠ {e}</li>
                    ))}
                  </ul>
                )}
              </div>
              <button onClick={() => setShowImportResult(null)}>
                <X className="h-4 w-4 text-success-text" />
              </button>
            </div>
          )}

          {/* Product DataTable */}
          <div className="px-6 py-4 pb-24">
            <div className="rounded-lg border overflow-hidden">
              {isFetching && products.length === 0 ? (
                <SkeletonTable rows={8} cols={6} />
              ) : products.length === 0 ? (
                <EmptyState icon={Package} title="No products found" />
              ) : (
                <div className={cn(isFetching && 'opacity-60 transition-opacity')}>
                  <Table mobileCards>
                    <THead>
                      <Tr hover={false}>
                        <Th className="w-12">
                          <Checkbox
                            checked={allPageSelected}
                            onChange={togglePage}
                            aria-label="Select all products on this page"
                          />
                        </Th>
                        <Th>Product</Th>
                        <Th className="w-32">SKU</Th>
                        <Th className="w-32">HS Code</Th>
                        <Th className="w-24 text-center">UOM</Th>
                        <Th className="w-36 text-center">Stock Type</Th>
                        <Th>Category</Th>
                        <Th>Tax</Th>
                        <Th className="w-36 text-end">Sale Price</Th>
                        <Th className="w-36 text-end">Cost Price</Th>
                      </Tr>
                    </THead>
                    <TBody>
                      {products.map((row) => (
                        <Tr
                          key={row.id}
                          className={cn('cursor-pointer', isSelected(row.id) && 'bg-primary/5')}
                          onClick={() => toggleRow(row)}
                        >
                          <Td>
                            <Checkbox
                              checked={isSelected(row.id)}
                              onChange={() => toggleRow(row)}
                              onClick={(e) => e.stopPropagation()}
                              aria-label={`Select ${row.name}`}
                            />
                          </Td>
                          <Td className="td-name">{productBody(row)}</Td>
                          <Td label="SKU">{skuBody(row)}</Td>
                          <Td label="HS Code">{hsCodeBody(row)}</Td>
                          <Td label="UOM" className="text-center">{uomBody(row)}</Td>
                          <Td label="Stock Type" className="text-center">{trackedBody(row)}</Td>
                          <Td label="Category">{categoryBody(row)}</Td>
                          <Td label="Tax">{taxBody(row)}</Td>
                          <Td label="Sale Price">{salePriceBody(row)}</Td>
                          <Td label="Cost Price">{costPriceBody(row)}</Td>
                        </Tr>
                      ))}
                    </TBody>
                  </Table>
                </div>
              )}
              <PaginationBar
                page={page}
                pageSize={50}
                total={meta?.total ?? 0}
                onPageChange={setPage}
                className="border-t px-4 py-3"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
